import { DocsPayload } from '../utils/docs-code';

export interface DocsParam {
  name: string;
  type: string;
  required?: boolean;
  accountScoped?: string;
  description: string;
  example?: string;
  defaultValue?: string;
  /** Only meaningful/sent for organizations flagged is_cikisi_user (see
   *  SessionService.organization) DocsApiReferenceDetail splits these
   *  out of the main Request body table into their own conditionally
   *  shown box rather than listing them for every reader, most of whom
   *  can't use them. */
  cikisiOnly?: boolean;
}

export interface DocsSchemaField {
  name: string;
  type: string;
  description: string;
}

export interface DocsSchema {
  name: string;
  /** Which endpoint's own page this schema is documented on see
   *  schemasFor(). */
  endpointId: string;
  /** Other endpoints that return it too (/similar returns /search's
   *  search_result), so their pages list it as well. */
  alsoFor?: string[];
  description?: string;
  fields: DocsSchemaField[];
}

export interface DocsEndpoint {
  id: string;
  method: 'POST';
  path: string;
  credit: number;
  tagline: string;
  category: 'find' | 'fetch';
  summary: string;
  returns: string;
  requestParams: DocsParam[];
  responseParams: DocsParam[];
  example: DocsPayload;
  sampleResponse: {
    messages: string[];
    data: Record<string, unknown>;
  };
}

const ACCOUNT_SCOPED_PARAMS: DocsParam[] = [
  {
    name: 'knowledge_base', type: 'boolean', defaultValue: 'false',
    description: 'Search the whole knowledge base. Independent of knowledge_topics below rather than a value inside it: this decides whether everything is in scope, not how many topics are picked.',
  },
  {
    name: 'knowledge_topics', type: 'array', accountScoped: 'collections',
    description: 'Restrict to these Knowledge Topic ids (positive integers). Ignored while knowledge_base is true.', example: '[7,19]',
  },
  {
    name: 'thesaurus_classes', type: 'array', accountScoped: 'thesaurus_classes',
    description: 'Restrict to these thesaurus class ids (positive integers).',
  },
  {
    name: 'saved_searches', type: 'array', accountScoped: 'saved_searches',
    description: 'Restrict to the perimeter of these saved search ids. A saved search is itself a set of filters, and it composes with the ones named here rather than replacing them.',
  },
  {
    name: 'source_ids', type: 'array', accountScoped: 'sources',
    description: 'Restrict to these source ids (positive integers).',
  },
  {
    name: 'tags', type: 'array', accountScoped: 'tags',
    description: 'Restrict to these tags. Accepts ids or names in the same list an entry that is all digits is read as an id, anything else as a name resolved against the account\'s own vocabulary.',
    example: '["Defense contracts",42]',
  },
  {
    name: 'source_categories', type: 'array', accountScoped: 'source_categories',
    description: 'Restrict to these source categories. Ids or names, as with tags.', example: '["Press releases"]',
  },
].map(p => ({ ...p, cikisiOnly: true }));

const PUBLISHED_DATE_PARAMS: DocsParam[] = [
  { name: 'published_from', type: 'string', description: 'Inclusive lower bound on publication date (ISO 8601). Must be ≤ published_to.', example: '"2026-02-01"' },
  { name: 'published_to', type: 'string', description: 'Inclusive upper bound on publication date (ISO 8601).', example: '"2026-08-01"' },
];

const GEO_DOMAIN_LANG_PARAMS: DocsParam[] = [
  {
    name: 'geolocated_areas', type: 'array',
    description: 'Areas to restrict the search to, as shape objects rather than references a shape lives inside whichever saved search drew it, so there is no registry to resolve an identifier against. Each carries a type and its coordinates, with an optional id the caller may use to recognise its own shapes in the echo. A Circle\'s coordinates are {lat, lon, radius} with the radius in metres. A Polygon\'s are an array of at least three {lat, lon} points, given in the order they enclose the area the ring is not closed for you. Points are objects rather than positional pairs a bare [42.6, 21.1] is two numbers whose order the reader has to know, and getting it backwards moves the search to another hemisphere without failing.',
    example: '[{"type":"Circle","coordinates":{"lat":42.68,"lon":21.12,"radius":28611.3}}]',
  },
  {
    name: 'include_domains', type: 'array',
    description: 'Only search these hosts. Bare hostnames no scheme, no path, no port; a leading "www." is stripped, and both spellings are matched. An unparseable entry is an error, not a skipped filter.',
    example: '["skanska.com"]',
  },
  {
    name: 'exclude_domains', type: 'array',
    description: 'Never return content from these hosts. Same format as include_domains.', example: '["google.com","linkedin.com"]',
  },
  {
    name: 'include_languages', type: 'array',
    description: 'Restrict to sources in these languages, by ISO 639-1 code (e.g. "en", "fr") not the language name. Ruling a language out is now naming the ones you want instead. A code the index does not know is an error rather than an ignored filter.',
    example: '["en","fr"]',
  },
];

const INDEXED_DATE_PARAMS: DocsParam[] = [
  { name: 'indexed_from', type: 'string', description: 'Inclusive lower bound on indexation date (ISO 8601). Must be ≤ indexed_to.' },
  { name: 'indexed_to', type: 'string', description: 'Inclusive upper bound on indexation date (ISO 8601).' },
];

const CIKISI_CONTENT_ONLY: DocsParam = {
  name: 'cikisi_content_only', type: 'boolean · enum', defaultValue: 'true',
  description: 'Must be true. Only Cikisi-indexed content is searchable through this pipeline; the parameter exists so the payload stays stable, and any other value is refused rather than quietly ignored.',
  cikisiOnly: true,
};

const SEARCH_IMAGE_DESCRIPTIONS: DocsParam = {
  name: 'search_image_descriptions', type: 'boolean · enum', defaultValue: 'false',
  description: 'Must be false. Image descriptions are not part of the searchable sentence index; a request asking for them is refused rather than silently answered without them.',
};

const RELEVANCE_CRITERIA_COUNT: DocsParam = {
  name: 'relevance_criteria_count', type: 'integer · enum', defaultValue: '5',
  description: 'How many relevance criteria enter the score, kept in order: terms, vectors, period, answer. One of 1, 2, 3, 4, 5.',
};

const USE_SOURCE_POPULARITY: DocsParam = {
  name: 'use_source_popularity', type: 'boolean', defaultValue: 'false',
  description: 'Add the source\'s reliability score to the relevance blend. On a linked search those coefficients are the customer\'s own client\'s; on a raw one they are the shared baseline.',
};

export const DOCS_ENDPOINTS: DocsEndpoint[] = [
  {
    id: 'rag', method: 'POST', path: '/api/rag', credit: 1,
    tagline: 'Returns passages that answer a question.',
    category: 'find',
    summary: 'Retrieval-augmented search over the Cikisi index: a natural-language question in, the passages that answer it out. Every snippet carries its English machine-translated text, the article URL and the publication date; each include_* flag adds one more field.',
    returns: 'rag_answer',
    requestParams: [
      { name: 'question', type: 'string', required: true, description: 'The question to answer. Must not be blank.', example: '"What are the key announcements of Skanska for last 6 months?"' },
      CIKISI_CONTENT_ONLY,
      ...ACCOUNT_SCOPED_PARAMS,
      ...PUBLISHED_DATE_PARAMS,
      ...GEO_DOMAIN_LANG_PARAMS,
      SEARCH_IMAGE_DESCRIPTIONS,
      // Grouped with "what to search" here rather than "how much/how to
      // return" below (Playground's own /rag form matches see rag.html)
      // even though they shape the search itself (how many snippets are
      // pulled in, how relevance is scored) rather than the output's shape,
      // unlike the include_* flags below, which are genuinely response-only.
      RELEVANCE_CRITERIA_COUNT,
      USE_SOURCE_POPULARITY,
      { name: 'comprehensiveness', type: 'integer', defaultValue: '120', description: 'Number of snippets processed and returned (1–500).' },
    ],
    responseParams: [
      { name: 'include_relevance_score', type: 'boolean', defaultValue: 'false', description: 'Add each snippet\'s relevance score.' },
      { name: 'include_original_language_snippet', type: 'boolean', defaultValue: 'false', description: 'Add the snippet in its original language.' },
      { name: 'include_article_title', type: 'boolean', defaultValue: 'false', description: 'Add the title of the article the snippet came from.' },
      { name: 'include_image_url', type: 'boolean', defaultValue: 'false', description: 'Add the URL of the article\'s image.' },
      { name: 'include_domain_name', type: 'boolean', defaultValue: 'false', description: 'Add the domain the article was published on.' },
      { name: 'include_favicon', type: 'boolean', defaultValue: 'false', description: 'Add the favicon URL for that domain.' },
    ],
    example: {
      request: {
        question: 'What are the key announcements of Skanska for last 6 months?',
        cikisi_content_only: true,
        knowledge_base: true,
        include_domains: [],
        exclude_domains: [],
        geolocated_areas: [],
        include_languages: ['en', 'fr'],
        relevance_criteria_count: 2,
        use_source_popularity: false,
        comprehensiveness: 120,
      },
      response: {
        include_relevance_score: true,
        include_original_language_snippet: true,
        include_article_title: true,
        include_image_url: true,
        include_domain_name: true,
        include_favicon: true,
      },
    },
    sampleResponse: {
      messages: ['Passages retrieved successfully'],
      data: {
        question: 'What are the key announcements of Skanska for last 6 months?',
        // Every optional field populated, matching example.response above
        // setting all six include_* flags to true a snippet with only
        // some of them present would misrepresent what that request
        // actually gets back.
        snippets: [
          {
            snippet: 'Skanska has been awarded a $420 million contract to build a new highway interchange, its largest infrastructure win of the year.',
            url: 'https://www.skanska.com/newsroom/press-releases/2026/skanska-wins-contract',
            published_at: '2026-05-12T09:00:00Z',
            relevance_score: 0.9123,
            original_language_snippet: 'Skanska har tilldelats ett kontrakt värt 420 miljoner dollar för att bygga en ny motorvägskorsning, dess största infrastrukturvinst under året.',
            article_title: 'Skanska wins major infrastructure contract',
            image_url: 'https://www.skanska.com/492x276/newsroom/2026/highway-interchange.jpg',
            domain_name: 'skanska.com',
            favicon: 'https://www.skanska.com/favicon.ico',
          },
          {
            snippet: 'The company also reported a 12% rise in quarterly revenue, driven largely by its civil engineering and residential construction divisions.',
            url: 'https://www.skanska.com/investors/reports/q2-2026-earnings',
            published_at: '2026-04-22T14:30:00Z',
            relevance_score: 0.8654,
            original_language_snippet: 'Bolaget rapporterade även en 12-procentig ökning av kvartalsomsättningen, till stor del driven av dess bygg- och bostadsdivisioner.',
            article_title: 'Skanska Q2 2026 earnings report',
            image_url: 'https://www.skanska.com/492x276/investors/2026/q2-earnings.jpg',
            domain_name: 'skanska.com',
            favicon: 'https://www.skanska.com/favicon.ico',
          },
        ],
        meta: { count: 2, most_frequent_answer: null },
      },
    },
  },
  {
    id: 'answer', method: 'POST', path: '/api/answer', credit: 1,
    tagline: 'Writes the answer in prose, with sources.',
    category: 'find',
    summary: 'Ask a question and get it answered in prose, written by a model from the passages /rag would have returned. The passages come back too, as sources, so an answer can be checked against what it was written from.',
    returns: 'answer_result',
    requestParams: [
      { name: 'question', type: 'string', required: true, description: 'The question to answer. Must not be blank.', example: '"What are the key announcements of Skanska for last 6 months?"' },
      CIKISI_CONTENT_ONLY,
      ...ACCOUNT_SCOPED_PARAMS,
      ...PUBLISHED_DATE_PARAMS,
      ...GEO_DOMAIN_LANG_PARAMS,
      SEARCH_IMAGE_DESCRIPTIONS,
    ],
    // Down to exactly the two controls the Playground's own "Response
    // configuration" card for /answer actually exposes (see answer.ts's
    // buildPayload(), which sends both under `response`) relevance_criteria_count/
    // use_source_popularity/comprehensiveness dropped: no Playground control
    // for them, so documenting them here read as usable when they weren't.
    // mode moved down from requestParams for the same reason it lives in
    // `response` on the wire: it's paired with output_type as "how the
    // answer is generated and shaped," not "what to search" reasoning is
    // fully live now, not the not-yet-implemented mode this used to say.
    responseParams: [
      {
        name: 'mode', type: 'string · enum', defaultValue: '"direct"',
        description: 'How the answer is arrived at. direct runs one retrieval and answers from it. reasoning runs several retrievals, each informed by the last. One of "direct", "reasoning".',
      },
      {
        name: 'output_type', type: 'string · enum', defaultValue: '"unstructured"',
        description: 'What answer comes back as. unstructured is natural-language prose. structured is an object carrying the model\'s own text, its confidence, and its claims with the source URLs each rests on. One of "unstructured", "structured".',
      },
    ],
    example: {
      // Deliberately minimal question plus exactly the two controls the
      // Playground's own "Response configuration" card for /answer
      // actually exposes (mode, output_type; see answer.ts's buildPayload()
      // and its own comment on why those two live under `response` rather
      // than `request`). Everything else documented above (cikisi_content_only,
      // knowledge_base, the domain/geo/language filters, relevance_criteria_count,
      // use_source_popularity, comprehensiveness) is a real accepted param
      // with no Playground control yet omitted here rather than shown
      // unable to be tried.
      request: {
        question: 'What are Apple\'s key product announcements from the last 6 months?',
        include_languages: ['en', 'fr'],
        include_domains: ['apple.com'],
        exclude_domains: ['reddit.com'],
        published_from: '2026-09-01',
        published_to: '2026-09-02',
      },
      response: {
        mode: 'direct',
        output_type: 'unstructured',
      },
    },
    sampleResponse: {
      messages: ['Answer returned successfully'],
      data: {
        question: 'What are Apple\'s key product announcements from the last 6 months?',
        answer: 'Apple announced the iPhone 17 lineup with a redesigned camera system, expanded its services bundle with a new AI-powered assistant tier, and confirmed a MacBook Pro refresh built on its next-generation silicon.',
        // Three shown (two English, one French, matching include_languages:
        // ['en', 'fr'] above) out of source_count's 71 the same "array is a
        // representative few, source_count is the real total" pattern the
        // real /answer response actually uses, not every source drawn from.
        sources: [
          {
            url: 'https://www.apple.com/newsroom/2026/06/apple-announces-iphone-17',
            title: 'Apple announces iPhone 17 lineup',
            published_at: '2026-06-09T17:00:00Z',
            domain_name: 'apple.com',
          },
          {
            url: 'https://www.apple.com/newsroom/2026/06/apple-expands-services-with-ai-assistant',
            title: 'Apple expands its services lineup with a new AI assistant tier',
            published_at: '2026-06-09T17:15:00Z',
            domain_name: 'apple.com',
          },
          {
            url: 'https://www.apple.com/fr/newsroom/2026/06/apple-confirme-le-nouveau-macbook-pro',
            title: 'Apple confirme un nouveau MacBook Pro avec sa puce de nouvelle génération',
            published_at: '2026-06-10T08:30:00Z',
            domain_name: 'apple.com',
          },
        ],
        meta: { mode: 'direct', output_type: 'unstructured', source_count: 71 },
      },
    },
  },
  {
    id: 'search', method: 'POST', path: '/api/search', credit: 1,
    tagline: 'Returns whole articles for a Boolean query.',
    category: 'find',
    summary: 'Boolean search over the index, returning articles rather than passages. The third sibling: /rag answers a question with the passages that support it, /answer writes prose from them, this matches a query and hands back rows. The caller chooses how wide each row is.',
    returns: 'search_result',
    requestParams: [
      { name: 'query', type: 'string', required: true, description: 'Boolean expression matched against the index AND, OR, NOT and "quoted phrases" mean what they look like.', example: '"Financial results 2025 Airbus OR defense contracts NATO"' },
      { name: 'apply_on_translated_content', type: 'boolean', defaultValue: 'false', description: 'Match against the English machine translation alongside the article\'s own words, so one English query reaches articles in every language. Left false, the match stays on what the article actually said.' },
      {
        name: 'ner', type: 'object',
        description: 'Named entities an article must mention, keyed by kind: organizations, persons, locations. Each value is an array of names, matched as sent. An unknown kind is refused; a kind whose value is not an array is an error rather than an ignored filter.',
        example: '{"organizations":["Safran","Thales","NATO"],"persons":["Elon Musk"],"locations":["Paris"]}',
      },
      {
        name: 'ner_operators', type: 'object',
        description: 'How the names within one kind combine AND means every name must appear in the same article, OR means any. Keyed by the same three kinds, each defaulting to OR.',
        example: '{"organizations":"OR","persons":"AND","locations":"OR"}',
      },
      CIKISI_CONTENT_ONLY,
      ...ACCOUNT_SCOPED_PARAMS,
      ...PUBLISHED_DATE_PARAMS,
      ...INDEXED_DATE_PARAMS,
      ...GEO_DOMAIN_LANG_PARAMS,
      {
        name: 'sort_by', type: 'string · enum', defaultValue: '"relevance"',
        description: 'Which order results come back in. One of "relevance", "publication_date", "indexation_date".',
      },
      { name: 'sort_direction', type: 'string · enum', defaultValue: '"desc"', description: 'One of "asc", "desc".' },
      {
        name: 'max_results', type: 'integer', defaultValue: '100',
        description: 'How many results to return (25–250). There is no paging: this endpoint answers "the best N for this query", and relevance ordering is not stable enough across calls to promise a second page that does not repeat or skip.',
      },
    ],
    responseParams: [
      // Flat include_* booleans instead of one `fields` object wrapping
      // them matches how /rag's own response fields (include_relevance_score,
      // include_article_title, ...) already work. include_url/include_title/
      // include_domain_name default true (omit `response` entirely and
      // that's what you get); every other field defaults false. Whatever's
      // off is absent from a result rather than returned as null.
      { name: 'include_url', type: 'boolean', defaultValue: 'true', description: 'Always true every result is a link, this can\'t be turned off.' },
      { name: 'include_title', type: 'boolean', defaultValue: 'true', description: 'The article\'s title.' },
      { name: 'include_domain_name', type: 'boolean', defaultValue: 'true', description: 'The domain the article was published on.' },
      { name: 'include_publication_date', type: 'boolean', defaultValue: 'false', description: 'When the source says it was published.' },
      { name: 'include_relevance_score', type: 'boolean', defaultValue: 'false', description: 'This result\'s relevance score.' },
      { name: 'include_favicon', type: 'boolean', defaultValue: 'false', description: 'Favicon URL for the article\'s domain.' },
      { name: 'include_indexation_date', type: 'boolean', defaultValue: 'false', description: 'When Cikisi indexed the article.' },
      { name: 'include_description', type: 'boolean', defaultValue: 'false', description: 'The article\'s meta description.' },
      { name: 'include_author', type: 'boolean', defaultValue: 'false', description: 'The article\'s byline, if known.' },
      { name: 'include_language', type: 'boolean', defaultValue: 'false', description: 'The article\'s detected language.' },
      { name: 'include_content', type: 'boolean', defaultValue: 'false', description: 'Full text, where the source\'s licence allows it to be served.' },
      { name: 'include_image_url', type: 'boolean', defaultValue: 'false', description: 'URL of the article\'s lead image.' },
    ],
    example: {
      request: {
        query: 'Financial results 2025 Airbus OR defense contracts NATO', apply_on_translated_content: false,
        ner: { organizations: ['Safran', 'Thales', 'NATO'], persons: ['Elon Musk'] },
        sort_by: 'relevance', sort_direction: 'desc', max_results: 100,
      },
      response: { include_url: true, include_title: true, include_domain_name: true, include_publication_date: false },
    },
    sampleResponse: {
      messages: ['Search completed successfully'],
      data: {
        query: 'Financial results 2025 Airbus OR defense contracts NATO',
        results: [
          {
            url: 'https://www.airbus.com/investors/financial-results-2025',
            title: 'Airbus reports full-year 2025 financial results',
            domain_name: 'airbus.com',
          },
          {
            url: 'https://www.thalesgroup.com/en/press-release/nato-defense-contract-2025',
            title: 'Thales awarded new NATO defense contract',
            domain_name: 'thalesgroup.com',
          },
        ],
        meta: { count: 2, total_matches: 214, sort_by: 'relevance', sort_direction: 'desc', fields: ['url', 'title', 'domain_name'] },
      },
    },
  },
  {
    id: 'image', method: 'POST', path: '/api/image', credit: 1,
    tagline: 'Returns images for a Boolean query.',
    category: 'find',
    summary: 'Image search over the index: a Boolean query matched against the pictures articles carry, their OCR text, filename, title, caption and keywords, returning the images rather than the articles. The fourth sibling: /rag answers with passages, /answer with prose, /search with articles, this with pictures. One result per picture: the same wire photo run by dozens of outlets comes back once.',
    returns: 'image_result',
    requestParams: [
      {
        name: 'query', type: 'string', required: true,
        description: 'Boolean expression. AND, OR, NOT, "quoted phrases" and parentheses mean what they look like. Matched against the image\'s own text, weighted OCR first, then filename and title, then caption and keywords. A malformed expression is refused with 422 naming query rather than failing inside the search.',
        example: '"strike AND (rail OR \\"public transport\\")"',
      },
      {
        name: 'expanded_title_search', type: 'boolean', defaultValue: 'true',
        description: 'Also match the query against the title of the article the image appeared in. On by default, as in Cikisi\'s own image search: a picture\'s own text is often thin, and the headline above it says what it shows. Switch off to match the picture alone.',
      },
      {
        name: 'must_include_persons', type: 'boolean', defaultValue: 'false',
        description: 'Only images where object detection found a PERSON or a FACE. A body or only a face both count.',
      },
      {
        name: 'countries', type: 'array',
        description: 'Countries the image is geolocated to, by its own metadata rather than the article\'s. Entries may be country ids, ISO codes or names, mixed in one list; one that resolves to no country is an error, not a dropped filter. Not account-scoped: a country belongs to nobody\'s account, so a raw search keeps it.',
        example: '["FR","Germany",83]',
      },
      {
        name: 'countries_operator', type: 'string · enum', defaultValue: '"OR"',
        description: 'How several countries combine: OR means any of them, AND means the image is placed in every one. One of "OR", "AND".',
      },
      CIKISI_CONTENT_ONLY,
      ...ACCOUNT_SCOPED_PARAMS,
      ...INDEXED_DATE_PARAMS,
      {
        name: 'sort_by', type: 'string · enum', defaultValue: '"relevance"',
        description: 'Which order results come back in, always descending: best match, newest article or most recently indexed first. The dates are the article\'s; an image has no publication date of its own. One of "relevance", "publication_date", "indexation_date".',
      },
      {
        name: 'max_results', type: 'integer', defaultValue: '100',
        description: 'How many images to return (25–250). No paging, as on /search. Counted after duplicates are collapsed, so the cap is spent on distinct pictures.',
      },
    ],
    responseParams: [
      // Sent inside response.fields, as in the example below.
      {
        name: 'include_url', type: 'boolean', defaultValue: 'true',
        description: 'The image. Always returned. Cikisi\'s stored copy where one exists (it keeps working after the publisher moves or hotlink-protects the file), the publisher\'s original otherwise.',
      },
      { name: 'include_indexation_date', type: 'boolean', description: 'When Cikisi first saw the article the image came from.' },
      {
        name: 'include_metadata', type: 'boolean',
        description: 'What is known about the picture (caption, keywords, OCR text, credit, place, dimensions, detected objects, dominant colours, the publisher\'s original url) and the article it appeared in.',
      },
    ],
    // A real run against dev, cut to two of its 100 results (and each
    // one's colours to the top three).
    example: {
      request: {
        query: 'Donald trump', expanded_title_search: true, must_include_persons: false, countries_operator: 'OR',
        sort_by: 'relevance', max_results: 100,
      },
      response: {
        fields: { include_url: true, include_indexation_date: false, include_metadata: true },
      },
    },
    sampleResponse: {
      messages: ['Results returned successfully'],
      data: {
        query: 'Donald trump',
        results: [
          {
            url: 'https://images2.cikisi.com/prod/2024-03/1134157347.jpeg',
            metadata: {
              original_url: 'https://www.computerworld.dk/fil/218722?width=600&height=315&focus=1',
              article: {
                url: 'https://www.computerworld.dk/art/286681/trump-udnytter-smutvej-til-at-boersnotere-truth-social-staar-til-at-tjene-milliarder-paa-noteringen?utm_source=RSS&utm_medium=RSS&utm_campaign=RSS',
                title: 'Trump udnytter smutvej til at børsnotere Truth Social: Står til at tjene milliarder på noteringen',
                domain_name: 'www.computerworld.dk',
                publication_date: '2024-03-25T09:45:00.000+00:00',
              },
              dimensions: { width: 600, height: 315 },
              detected: { persons: 1, faces: 0, text: 6, animals: 0, sports: 0, vehicles: 0 },
              colors: [{ name: 'white', ratio: 63 }, { name: 'black', ratio: 16 }, { name: 'red', ratio: 6 }],
              ocr: 'Trump Donald Trump Donald',
            },
          },
          {
            url: 'https://images2.cikisi.com/prod/2025-12/1524876234.jpeg',
            metadata: {
              original_url: 'https://www.doingbuzz.com/wp-content/uploads/2025/12/donald-trump-prix-paix-fifa-8154d2-1@1x.jpeg',
              article: {
                url: 'https://www.doingbuzz.com/washington-donald-trump-recoit-le-tout-premier-prix-de-la-paix-instaure-par-la-fifa/',
                title: 'Washington : Donald Trump reçoit le tout premier « Prix de la paix » instauré par la FIFA',
                domain_name: 'www.doingbuzz.com',
                publication_date: '2025-12-07T02:41:38.000Z',
              },
              dimensions: { width: 569, height: 320 },
              detected: { persons: 2, faces: 0, text: 4, animals: 0, sports: 0, vehicles: 0 },
              colors: [{ name: 'black', ratio: 33 }, { name: 'red', ratio: 20 }, { name: 'grey', ratio: 15 }],
              title: 'FBL-WC-2026-DRAW',
              description: '(L-R) US President Donald Trump receives the FIFA Peace Prize from Italian Fifa President Gianni Infantino during the draw for the 2026 FIFA Football World Cup taking place in the US, Canada and Mexico, at the Kennedy Center, in Washington, DC, on December 5, 2025. (Photo by Jim WATSON / AFP)',
              keywords: ['Horizontal'],
              ocr: 'DONALD TRUMP',
              artist: 'JIM WATSON',
              copyright: 'Afp or licensors',
              source: 'AFP',
              date: '2025-12-05T16:30:34.000Z',
              city: 'Washington',
              province: 'District of Columbia',
              country: 'United States',
            },
          },
        ],
        meta: { count: 100, total_matches: 2428818, sort_by: 'relevance', fields: ['url', 'metadata'] },
      },
    },
  },
  {
    id: 'similar', method: 'POST', path: '/api/similar', credit: 1,
    tagline: 'Returns articles that read like a piece of text.',
    category: 'find',
    summary: 'Similar-content search over the index: a piece of text in (a headline, a paragraph, a whole article) and the articles that read most like it out. A sibling of /search: the same scopes and the same response configuration, with text to resemble in place of a Boolean query.',
    returns: 'search_result',
    requestParams: [
      {
        name: 'query', type: 'string', required: true,
        description: 'Plain text, not a Boolean expression: AND, OR, quotes and parentheses are read as words. Elasticsearch picks the text\'s most distinctive terms and returns the articles sharing the most of them, comparing title, description and content; an article must share at least 30% of them to count. Up to 20,000 characters, so a whole article may be pasted.',
        example: '"Dock workers in Rotterdam walked out over automation plans at the container terminals"',
      },
      {
        name: 'countries', type: 'array',
        description: 'Countries the article is about: the locations it mentions, rather than an image\'s geolocation as on /image. Entries may be country ids, ISO codes or names, mixed in one list; one that resolves to no country is an error, not a dropped filter. Not account-scoped, so a raw search keeps it.',
        example: '["NL","Belgium",83]',
      },
      {
        name: 'countries_operator', type: 'string · enum', defaultValue: '"OR"',
        description: 'How several countries combine: OR means any of them, AND means the article is about every one. One of "OR", "AND".',
      },
      GEO_DOMAIN_LANG_PARAMS[0],
      CIKISI_CONTENT_ONLY,
      ...ACCOUNT_SCOPED_PARAMS,
      ...INDEXED_DATE_PARAMS,
      {
        name: 'sort_by', type: 'string · enum', defaultValue: '"relevance"',
        description: 'Which order results come back in. One of "relevance", "publication_date", "indexation_date".',
      },
      { name: 'sort_direction', type: 'string · enum', defaultValue: '"desc"', description: 'One of "asc", "desc".' },
      {
        name: 'max_results', type: 'integer', defaultValue: '100',
        description: 'How many results to return (25–250). There is no paging: this endpoint answers "the best N for this query", and relevance ordering is not stable enough across calls to promise a second page that does not repeat or skip.',
      },
    ],
    responseParams: [
      // Sent inside response.fields, as in the example below; the same
      // columns as /search.
      { name: 'include_url', type: 'boolean', defaultValue: 'true', description: 'The article\'s link. Always returned.' },
      { name: 'include_title', type: 'boolean', description: 'The article\'s headline.' },
      { name: 'include_domain_name', type: 'boolean', description: 'The host it was published on.' },
      { name: 'include_publication_date', type: 'boolean', description: 'When the source says it was published.' },
      { name: 'include_relevance_score', type: 'boolean', description: 'How well the article matched.' },
      { name: 'include_favicon', type: 'boolean', description: 'Conventional /favicon.ico on the article\'s own host.' },
      { name: 'include_indexation_date', type: 'boolean', description: 'When Cikisi first saw the article.' },
      { name: 'include_description', type: 'boolean', description: 'The article\'s summary or standfirst.' },
      { name: 'include_author', type: 'boolean', description: 'The byline, where the source carries one.' },
      { name: 'include_language', type: 'boolean', description: 'The language the article was published in.' },
      { name: 'include_content', type: 'boolean', description: 'The article\'s text.' },
      { name: 'include_image_url', type: 'boolean', description: 'The article\'s lead image.' },
    ],
    // A real run against dev, cut to two of its 100 results.
    example: {
      request: { query: 'Donald trump?', countries_operator: 'OR', sort_by: 'relevance', sort_direction: 'desc', max_results: 100 },
      response: {
        fields: { include_url: true, include_title: true, include_domain_name: true },
      },
    },
    sampleResponse: {
      messages: ['Results returned successfully'],
      data: {
        query: 'Donald trump?',
        results: [
          {
            url: 'https://www.buzzfeed.com/alexalisitza/trump-name-one-million-times',
            title: 'I Tried Writing Trump\'s Name 1 Million Times To Help Visualize THAT Epstein Allegation',
            domain_name: 'www.buzzfeed.com',
          },
          {
            url: 'https://slate.com/news-and-politics/2026/02/epstein-files-donald-trump-is-referenced-a-full-38-000-times.html?via=rss',
            title: 'The Epstein Files Contain More Than 38,000 Trump References. Here’s a Helpful Visualization.',
            domain_name: 'slate.com',
          },
        ],
        meta: { count: 100, total_matches: 39541065, sort_by: 'relevance', sort_direction: 'desc', fields: ['url', 'title', 'domain_name'] },
      },
    },
  },
];

export const DOCS_SCHEMAS: DocsSchema[] = [
  {
    name: 'rag_answer', endpointId: 'rag', description: 'The data object of a /rag response.',
    fields: [
      { name: 'question', type: 'string', description: 'The question as it was received, trimmed.' },
      { name: 'snippets', type: 'array<snippet>', description: 'Best match first, cut to comprehensiveness.' },
      { name: 'meta', type: 'rag_meta', description: '' },
    ],
  },
  {
    name: 'rag_meta', endpointId: 'rag',
    fields: [
      { name: 'count', type: 'integer', description: 'Number of snippets returned.' },
      { name: 'most_frequent_answer', type: 'string · nullable', description: 'The answer seen most often across the matching passages.' },
      { name: 'parameters_echo', type: 'object', description: 'The request as Cikisi normalized it, defaults applied an echo a consumer can paste straight back into a request.' },
    ],
  },
  {
    name: 'snippet', endpointId: 'rag', description: 'One extracted passage. The first three fields are always present; each of the others appears only when its include_* flag was set on the request.',
    fields: [
      { name: 'snippet', type: 'string', description: 'English machine-translated text of the passage.' },
      { name: 'url', type: 'string', description: 'Link to the article the passage was extracted from.' },
      { name: 'published_at', type: 'datetime', description: 'When the source published the article.' },
      { name: 'relevance_score', type: 'number · nullable', description: '0–1, rounded to four places. With include_relevance_score.' },
      { name: 'original_language_snippet', type: 'string · nullable', description: 'The passage in its original language. With include_original_language_snippet.' },
      { name: 'article_title', type: 'string · nullable', description: 'With include_article_title.' },
      { name: 'image_url', type: 'string · nullable', description: 'With include_image_url.' },
      { name: 'domain_name', type: 'string · nullable', description: 'With include_domain_name.' },
      { name: 'favicon', type: 'string · nullable', description: 'Conventional /favicon.ico on the article\'s own host. With include_favicon.' },
    ],
  },
  {
    name: 'answer_result', endpointId: 'answer', description: 'The data object of an /answer response.',
    fields: [
      { name: 'question', type: 'string', description: 'The question as it was received, trimmed.' },
      { name: 'answer', type: 'object · nullable', description: 'Prose when output_type is unstructured, a structured_answer object when it is structured. Null when nothing was retrieved, the model returned nothing, or structured only its reply would not parse as JSON.' },
      { name: 'sources', type: 'array<answer_source>', description: 'The articles the passages came from, one entry per article. Present whether or not the model produced anything.' },
      { name: 'meta', type: 'answer_meta', description: '' },
    ],
  },
  {
    name: 'structured_answer', endpointId: 'answer', description: 'The answer object when output_type is structured.',
    fields: [
      { name: 'text', type: 'string · nullable', description: 'The answer in prose, as the model wrote it.' },
      { name: 'confidence', type: 'number · nullable', description: 'The model\'s own estimate, 0–1 to two places.' },
      { name: 'claims', type: 'array<answer_claim>', description: 'The individual statements the answer rests on.' },
    ],
  },
  {
    name: 'answer_claim', endpointId: 'answer',
    fields: [
      { name: 'statement', type: 'string', description: 'One claim made by the answer.' },
      { name: 'source_urls', type: 'array<string>', description: 'URLs backing this claim. A passage id the model invented is dropped rather than turned into a link an empty list means nothing retrieved supported it.' },
    ],
  },
  {
    name: 'answer_source', endpointId: 'answer', description: 'An article the answer was written from.',
    fields: [
      { name: 'url', type: 'string', description: '' },
      { name: 'title', type: 'string · nullable', description: '' },
      { name: 'published_at', type: 'datetime · nullable', description: '' },
      { name: 'domain_name', type: 'string · nullable', description: '' },
    ],
  },
  {
    name: 'answer_meta', endpointId: 'answer',
    fields: [
      { name: 'mode', type: 'string', description: 'The mode the answer was produced with.' },
      { name: 'output_type', type: 'string', description: 'The output type that was applied.' },
      { name: 'source_count', type: 'integer', description: 'Number of distinct articles in sources.' },
      { name: 'answer_parsed', type: 'boolean · nullable', description: 'Structured requests only. False means the model replied but its reply would not parse; absent means it was never asked.' },
      { name: 'parameters_echo', type: 'object', description: 'The request as Cikisi normalized it, defaults applied.' },
    ],
  },
  {
    name: 'search_result', endpointId: 'search', alsoFor: ['similar'], description: 'The data object of a /search response.',
    fields: [
      { name: 'query', type: 'string', description: 'The query as it was received, trimmed.' },
      { name: 'results', type: 'array<search_hit>', description: '' },
      { name: 'meta', type: 'search_meta', description: '' },
    ],
  },
  {
    name: 'search_hit', endpointId: 'search', alsoFor: ['similar'], description: 'One article. url is always present; every other field appears only when it was named in fields, and is omitted rather than null when the article does not carry it.',
    fields: [
      { name: 'url', type: 'string', description: 'Always present. A hit without one is dropped rather than returned incomplete.' },
      { name: 'title', type: 'string · nullable', description: '' },
      { name: 'domain_name', type: 'string · nullable', description: 'The host the article was published on.' },
      { name: 'publication_date', type: 'datetime · nullable', description: 'When the source says it was published.' },
      { name: 'indexation_date', type: 'datetime · nullable', description: 'When Cikisi saw it later than publication_date, sometimes much later.' },
      { name: 'relevance_score', type: 'number · nullable', description: 'Elasticsearch\'s own score for this hit, not normalized to 0–1.' },
      { name: 'favicon', type: 'string · nullable', description: 'Conventional /favicon.ico on the article\'s own host.' },
      { name: 'description', type: 'string · nullable', description: '' },
      { name: 'author', type: 'string · nullable', description: '' },
      { name: 'language', type: 'string · nullable', description: '' },
      { name: 'content', type: 'string · nullable', description: 'Full text, where the source\'s licence allows it to be served.' },
      { name: 'image_url', type: 'string · nullable', description: '' },
    ],
  },
  {
    name: 'search_meta', endpointId: 'search', alsoFor: ['similar'],
    fields: [
      { name: 'count', type: 'integer', description: 'Results returned, after max_results was applied.' },
      { name: 'total_matches', type: 'integer', description: 'How many documents matched before the size cut the figure to page against, not the length of results.' },
      { name: 'sort_by', type: 'string', description: '' },
      { name: 'sort_direction', type: 'string', description: '' },
      { name: 'fields', type: 'array<string>', description: 'The columns actually returned, url first and always.' },
      { name: 'parameters_echo', type: 'object', description: 'The request as Cikisi normalized it, defaults applied.' },
    ],
  },
  {
    name: 'image_result', endpointId: 'image', description: 'The data object of an /image response.',
    fields: [
      { name: 'query', type: 'string', description: 'The query as it was received, trimmed.' },
      { name: 'results', type: 'array<image_hit>', description: '' },
      { name: 'meta', type: 'image_meta', description: '' },
    ],
  },
  {
    name: 'image_hit', endpointId: 'image', description: 'One distinct picture. url is always present; the other fields appear only when switched on.',
    fields: [
      { name: 'url', type: 'string', description: 'Cikisi\'s stored copy where one exists, the publisher\'s original otherwise. A hit with neither is dropped rather than returned.' },
      { name: 'indexation_date', type: 'datetime · nullable', description: 'When Cikisi saw the article the image came from.' },
      { name: 'metadata', type: 'image_metadata · nullable', description: '' },
    ],
  },
  {
    name: 'image_metadata', endpointId: 'image', description: 'What enrichment found in the picture. Blank values are left out rather than sent as empty strings, so "no caption" is an absent key, not one to render.',
    fields: [
      { name: 'original_url', type: 'string · nullable', description: 'The publisher\'s own url for the file, for attribution.' },
      { name: 'article', type: 'image_article · nullable', description: '' },
      { name: 'dimensions', type: 'image_dimensions · nullable', description: '' },
      { name: 'detected', type: 'image_detected · nullable', description: 'Absent when the image was never analysed, which is not the same as zero of everything.' },
      { name: 'colors', type: 'array<image_color> · nullable', description: 'Colours actually present, dominant first.' },
      { name: 'title', type: 'string · nullable', description: '' },
      { name: 'description', type: 'string · nullable', description: 'The caption.' },
      { name: 'keywords', type: 'array<string> · nullable', description: '' },
      { name: 'ocr', type: 'string · nullable', description: 'Text read off the picture.' },
      { name: 'artist', type: 'string · nullable', description: '' },
      { name: 'copyright', type: 'string · nullable', description: '' },
      { name: 'source', type: 'string · nullable', description: '' },
      { name: 'date', type: 'string · nullable', description: 'The date in the image\'s own metadata, as stored.' },
      { name: 'city', type: 'string · nullable', description: '' },
      { name: 'province', type: 'string · nullable', description: '' },
      { name: 'country', type: 'string · nullable', description: '' },
    ],
  },
  {
    name: 'image_article', endpointId: 'image', description: 'The article the image appeared in.',
    fields: [
      { name: 'url', type: 'string · nullable', description: '' },
      { name: 'title', type: 'string · nullable', description: '' },
      { name: 'domain_name', type: 'string · nullable', description: '' },
      { name: 'publication_date', type: 'datetime · nullable', description: '' },
    ],
  },
  {
    name: 'image_dimensions', endpointId: 'image',
    fields: [
      { name: 'width', type: 'integer', description: '' },
      { name: 'height', type: 'integer', description: '' },
    ],
  },
  {
    name: 'image_detected', endpointId: 'image', description: 'Object detection counts per class. Zero is an answer: the detector looked and found none.',
    fields: [
      { name: 'persons', type: 'integer', description: '' },
      { name: 'faces', type: 'integer', description: '' },
      { name: 'text', type: 'integer', description: '' },
      { name: 'animals', type: 'integer', description: '' },
      { name: 'sports', type: 'integer', description: '' },
      { name: 'vehicles', type: 'integer', description: 'Cars, motorcycles, aeroplanes, buses, trains and trucks, summed.' },
    ],
  },
  {
    name: 'image_color', endpointId: 'image',
    fields: [
      { name: 'name', type: 'string', description: '' },
      { name: 'ratio', type: 'integer', description: 'Share of the picture in this colour.' },
    ],
  },
  {
    name: 'image_meta', endpointId: 'image',
    fields: [
      { name: 'count', type: 'integer', description: 'Images returned, after duplicates were collapsed and max_results applied.' },
      { name: 'total_matches', type: 'integer', description: 'Articles that matched, counted before collapsing, so it can exceed the number of distinct images there are to return.' },
      { name: 'sort_by', type: 'string', description: '' },
      { name: 'fields', type: 'array<string>', description: 'The fields actually returned, url first and always.' },
      { name: 'parameters_echo', type: 'object', description: 'The request as Cikisi normalized it, defaults applied.' },
    ],
  },
];

/** This endpoint's own schemas, in DOCS_SCHEMAS order what
 *  DocsApiReferenceDetail renders under "Response schemas" so each
 *  endpoint's page is self-contained instead of sharing one combined
 *  section across all three. */
export function schemasFor(endpointId: string): DocsSchema[] {
  return DOCS_SCHEMAS.filter(s => s.endpointId === endpointId || s.alsoFor?.includes(endpointId));
}
